import { useState, useRef, useEffect, useCallback } from 'react';
import styles from './ChatPage.module.css';
import { apiFetch } from '../api/client';

interface Turn {
  id?: string;
  role: 'user' | 'assistant';
  text: string;
  typing?: boolean;
  proposals?: Proposal[];
}

interface Proposal {
  type: string;
  description: string;
  params?: Record<string, unknown>;
}

const SEEDS = [
  'Muéstrame leads CRITICAL sin contacto',
  'Enriquece Congalsa',
  '¿Cuántos leads en etapa outreach?',
  'Sugiere follow-up para leads inactivos',
];

const ACTION_LABELS: Record<string, string> = {
  enrich: 'Enriquecer',
  generate_outreach: 'Redactar outreach',
  suggest_followup: 'Sugerir follow-up',
  query_leads: 'Consultar leads',
  update_lead: 'Actualizar lead',
};

function formatTime(d = new Date()): string {
  try {
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export default function ChatPage() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showSeeds, setShowSeeds] = useState(true);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const transcriptRef = useRef<HTMLOListElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [turns, scrollToBottom]);

  function addTurn(turn: Turn) {
    setShowSeeds(false);
    setTurns((prev) => [...prev, turn]);
  }

  function removeTyping() {
    setTurns((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'assistant' && last.typing) {
        return prev.slice(0, -1);
      }
      return prev;
    });
  }

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (sending) return;
    const message = input.trim();
    if (!message) return;

    setSending(true);
    setInput('');
    addTurn({ role: 'user', text: message });
    addTurn({ role: 'assistant', text: '', typing: true });

    try {
      const data = await apiFetch('/api/chat', {
        method: 'POST',
        body: JSON.stringify({ message }),
      }) as {
        success?: boolean;
        response?: { text?: string; proposedActions?: Proposal[] };
        error?: string;
      };

      removeTyping();

      if (data?.success && data.response) {
        addTurn({
          role: 'assistant',
          text: data.response.text || '',
          proposals: data.response.proposedActions || [],
        });
      } else {
        addTurn({
          role: 'assistant',
          text: 'Hubo un error procesando tu consulta. Intenta de nuevo.',
        });
      }
    } catch {
      removeTyping();
      addTurn({
        role: 'assistant',
        text: 'Hubo un error de conexión. Intenta de nuevo.',
      });
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }

  async function handleProposal(actionType: string, params?: Record<string, unknown>) {
    switch (actionType) {
      case 'enrich':
      case 'generate_outreach':
      case 'suggest_followup': {
        const leadId = params?.leadId as string | undefined;
        if (leadId) {
          window.location.href = `/leads/${encodeURIComponent(leadId)}`;
        } else {
          window.alert('Selecciona un lead primero');
        }
        break;
      }
      case 'query_leads': {
        const q: string[] = [];
        if (params?.stage) q.push(`stage=${encodeURIComponent(String(params.stage))}`);
        if (params?.sector) q.push(`sector=${encodeURIComponent(String(params.sector))}`);
        if (params?.priority) q.push(`priority=${encodeURIComponent(String(params.priority))}`);
        if (params?.tier) q.push(`tier=${encodeURIComponent(String(params.tier))}`);
        if (params?.search) q.push(`search=${encodeURIComponent(String(params.search))}`);
        window.location.href = `/library${q.length ? `?${q.join('&')}` : ''}`;
        break;
      }
      case 'update_lead': {
        const id = params?.id as string | undefined;
        if (!id) {
          window.alert('No se especificó el lead');
          return;
        }
        if (!window.confirm('¿Confirmar actualización del lead?')) return;
        try {
          await apiFetch(`/api/leads/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(params),
          });
          window.alert('Lead actualizado');
        } catch {
          window.alert('Error al actualizar lead');
        }
        break;
      }
      default:
        window.alert('Acción no reconocida');
    }
  }

  return (
    <section className={styles.shell} aria-label="Asistente de redacción">
      <header className={styles.masthead}>
        <div className={styles.mastheadWrap}>
          <p className={styles.kicker}>Asistente de redacción</p>
          <h1 className={styles.head}>Conversación</h1>
          <p className={styles.deck}>
            Consulta leads, enriquece registros o redacta outreach. Cada respuesta
            queda anotada abajo, con las acciones propuestas al margen.
          </p>
        </div>
      </header>

      <div className={styles.body}>
        {showSeeds && (
          <section className={styles.seeds} aria-label="Sugerencias para empezar">
            <p className={styles.seedsKicker}>Empieza por…</p>
            <ul className={styles.seedsList}>
              {SEEDS.map((seed) => (
                <li key={seed}>
                  <button
                    type="button"
                    className={styles.seed}
                    onClick={() => {
                      setInput(seed);
                      inputRef.current?.focus();
                    }}
                  >
                    {seed}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <ol
          ref={transcriptRef}
          className={styles.transcript}
          aria-live="polite"
          aria-label="Conversación"
        >
          {turns.map((turn, i) => (
            <li
              key={turn.id ?? `${turn.role}-${i}`}
              className={`${styles.turn} ${turn.role === 'user' ? styles.turnUser : styles.turnAssistant}`}
            >
              <div className={styles.attr}>
                <span className={styles.attrWho}>
                  {turn.role === 'user' ? 'Tú' : 'Asistente'}
                </span>
                <span className={styles.attrWhen}>{formatTime()}</span>
              </div>
              <div className={styles.turnBody}>
                {turn.typing ? (
                  <div className={styles.typing} aria-label="El asistente está escribiendo">
                    <span /><span /><span />
                  </div>
                ) : (
                  <>
                    <p className={styles.text}>{turn.text}</p>
                    {turn.proposals && turn.proposals.length > 0 && (
                      <div className={styles.proposals}>
                        <p className={styles.proposalsKicker}>Acciones propuestas</p>
                        <ul className={styles.proposalsList}>
                          {turn.proposals.map((p, pi) => (
                            <li key={pi} className={styles.proposal}>
                              <span className={styles.proposalText}>
                                <em>{ACTION_LABELS[p.type] || p.type}.</em> {p.description}
                              </span>
                              <button
                                type="button"
                                className={styles.proposalAction}
                                onClick={() => handleProposal(p.type, p.params)}
                              >
                                Ejecutar
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </li>
          ))}
          <div ref={bottomRef} />
        </ol>
      </div>

      <form className={styles.composer} onSubmit={handleSubmit} autoComplete="off">
        <label htmlFor="cht-input" className={styles.composerLabel}>
          Escribe
        </label>
        <div className={styles.composerRow}>
          <textarea
            ref={inputRef}
            id="cht-input"
            className={styles.input}
            name="message"
            rows={1}
            placeholder="Escribe una consulta o instrucción…"
            aria-label="Mensaje"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
            style={{ minHeight: '1.6rem', maxHeight: '8rem' }}
          />
          <button
            type="submit"
            className={styles.send}
            aria-label="Enviar"
            disabled={sending || !input.trim()}
          >
            <span className={styles.sendLabel}>Enviar</span>
          </button>
        </div>
      </form>
    </section>
  );
}
